import { create } from 'zustand';
import type { HubHealthStatus, HubHealthIndicator } from '../types/hub';
import { checkHubHealth, getHubPreferences } from '../services/api';

interface HubHealthStore {
  healthStatus: HubHealthStatus | null;
  healthIndicator: HubHealthIndicator | null;
  isLoading: boolean;
  lastCheck: Date | null;
  error: string | null;
  checkHealth: () => Promise<void>;
  startPeriodicCheck: () => void;
  stopPeriodicCheck: () => void;
}

export const useHubHealthStore = create<HubHealthStore>((set) => {
  let checkInterval: NodeJS.Timeout | null = null;

  const checkHealth = async () => {
    set({ isLoading: true, error: null });
    try {
      const healthStatus = await checkHubHealth();
      
      // Create health indicator from status
      const healthIndicator: HubHealthIndicator = {
        mqtt: healthStatus.services.mqtt.status === 'connected' ? 'connected' : 'disconnected',
        websocket: healthStatus.services.websocket.status === 'running' ? 'running' : 'stopped',
        database: healthStatus.services.database.status === 'healthy' ? 'healthy' : 'unhealthy',
        api: healthStatus.status === 'healthy' ? 'running' : 'down'  // API status is overall system status
      };

      set({ 
        healthStatus, 
        healthIndicator, 
        isLoading: false, 
        lastCheck: new Date() 
      });
    } catch (error) {
      // If we can't reach the health endpoint, all services are down
      const healthIndicator: HubHealthIndicator = {
        mqtt: 'disconnected',
        websocket: 'stopped', 
        database: 'unhealthy',
        api: 'down'
      };
      
      set({ 
        error: error instanceof Error ? error.message : 'Unknown error',
        healthIndicator,
        isLoading: false,
        lastCheck: new Date()
      });
    }
  };

  const startPeriodicCheck = async () => {
    // Stop existing interval if running
    if (checkInterval) {
      clearInterval(checkInterval);
    }

    // Do initial check
    await checkHealth();

    // Get interval from backend preferences
    try {
      const prefs = await getHubPreferences();
      const intervalSeconds = prefs.HEALTH_CHECK_INTERVAL || 30;
      const intervalMs = intervalSeconds * 1000;

      // Start periodic checking
      checkInterval = setInterval(() => {
        checkHealth();
      }, intervalMs);
    } catch (error) {
      console.error('Failed to get preferences for health check interval:', error);
      // Fallback to 30 seconds
      checkInterval = setInterval(() => {
        checkHealth();
      }, 30000);
    }
  };

  const stopPeriodicCheck = () => {
    if (checkInterval) {
      clearInterval(checkInterval);
      checkInterval = null;
    }
  };

  return {
    healthStatus: null,
    healthIndicator: null,
    isLoading: false,
    lastCheck: null,
    error: null,
    checkHealth,
    startPeriodicCheck,
    stopPeriodicCheck
  };
});

export default useHubHealthStore; 